import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { useAuth } from '../auth/AuthProvider'
import type { DashboardUser } from '../auth/auth-adapter'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import type { NexHealthOnboardingResponse } from './onboarding-nexhealth-contracts'
import {
  onboardingNexHealthQueryKey,
  onboardingNexHealthQueryOptions,
  onboardingStatusQueryKey,
  onboardingStatusQueryOptions,
} from './onboarding-query'
import { destinationPath, routeOnboardingResponse } from './onboarding-routing'
import styles from './NexHealthPage.module.css'

type Feedback =
  | 'credential-rejected'
  | 'invalid'
  | 'no-locations'
  | 'pending'
  | 'unavailable'
  | null

export function NexHealthConnectionPage({
  client = getOnboardingApiClient(),
}: {
  client?: OnboardingApiClient
}) {
  const auth = useAuth()
  if (auth.status !== 'authenticated') {
    return <Spinner label="Restoring your setup" />
  }
  return (
    <AuthenticatedConnectionPage
      key={auth.user.uid}
      client={client}
      user={auth.user}
    />
  )
}

function AuthenticatedConnectionPage({
  client,
  user,
}: {
  client: OnboardingApiClient
  user: DashboardUser
}) {
  const auth = useAuth()
  const location = useLocation()
  const statusQuery = useQuery(onboardingStatusQueryOptions(client, user))
  if (statusQuery.isPending || statusQuery.isFetching) {
    return <Spinner label="Checking your setup" />
  }
  if (statusQuery.isError) {
    if (
      statusQuery.error instanceof OnboardingApiError &&
      statusQuery.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    return (
      <StatePanel
        kind="error"
        title="NexHealth setup unavailable"
        description="Retry the setup check before entering a credential."
      />
    )
  }
  const destination = routeOnboardingResponse(statusQuery.data)
  if (destination !== 'nexhealth') {
    return <Navigate to={destinationPath(destination)} replace />
  }
  return (
    <>
      <p>Connect your PMS for patient and appointment actions, or use informational Voice with Flio-owned clinic hours.</p>
      {'status' in statusQuery.data && statusQuery.data.status === 'CRM_CREDENTIAL_REQUIRED' && <Link to="/onboarding/without-pms">Continue without PMS</Link>}
      <ConnectionEditor client={client} user={user} onSignOut={auth.logout} />
    </>
  )
}

function ConnectionEditor({
  client,
  user,
  onSignOut,
}: {
  client: OnboardingApiClient
  user: DashboardUser
  onSignOut: () => Promise<void>
}) {
  const queryClient = useQueryClient()
  const query = useQuery(onboardingNexHealthQueryOptions(client, user))
  const [subdomain, setSubdomain] = useState('')
  const [credential, setCredential] = useState('')
  const [replacing, setReplacing] = useState(false)
  const [working, setWorking] = useState(false)
  const [feedback, setFeedback] = useState<Feedback>(null)

  const applyResponse = async (response: NexHealthOnboardingResponse) => {
    queryClient.setQueryData(onboardingNexHealthQueryKey(user.uid), response)
    await queryClient.invalidateQueries({
      queryKey: onboardingStatusQueryKey(user.uid),
    })
    if (response.errorCode === 'no_locations') {
      setFeedback('no-locations')
    }
  }

  const handleError = async (error: unknown) => {
    setCredential('')
    if (error instanceof OnboardingApiError) {
      if (error.code === 'credential-rejected') {
        setFeedback('credential-rejected')
      } else if (error.code === 'invalid-nexhealth') {
        setFeedback('invalid')
      } else if (error.code === 'operation-in-progress') {
        setFeedback('pending')
      } else {
        setFeedback('unavailable')
      }
    } else {
      setFeedback('unavailable')
    }
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: onboardingNexHealthQueryKey(user.uid),
      }),
      queryClient.invalidateQueries({
        queryKey: onboardingStatusQueryKey(user.uid),
      }),
    ])
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (working || !query.data) return
    if (
      !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(subdomain) ||
      !credential.trim()
    ) {
      setFeedback('invalid')
      return
    }
    setWorking(true)
    setFeedback(null)
    try {
      const response = await client.connectNexHealth(
        user,
        query.data.revision,
        subdomain,
        credential
      )
      setCredential('')
      setReplacing(false)
      setSubdomain(response.subdomain ?? subdomain.toLowerCase())
      await applyResponse(response)
    } catch (error) {
      await handleError(error)
    } finally {
      setWorking(false)
    }
  }

  const retryDiscovery = async () => {
    if (working || !query.data) return
    setWorking(true)
    setFeedback(null)
    try {
      await applyResponse(
        await client.discoverNexHealth(user, query.data.revision)
      )
    } catch (error) {
      await handleError(error)
    } finally {
      setWorking(false)
    }
  }

  if (query.isPending) return <Spinner label="Loading NexHealth connection" />
  if (query.isError) {
    return (
      <Card className={styles.panel}>
        <StatePanel
          kind="error"
          title="NexHealth connection unavailable"
          description="Retry this check. No credential needs to be entered until the page loads safely."
        />
        <Button type="button" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </Card>
    )
  }

  const showForm = !query.data.canRetryDiscovery || replacing
  const storedSubdomain = query.data.subdomain ?? ''

  return (
    <Card className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.eyebrow}>NexHealth connection</span>
        <h1>Connect your scheduling account</h1>
        <p>
          Connect your NexHealth account so Flio can access the scheduling
          resources required for setup.
        </p>
      </div>

      {(feedback === 'credential-rejected' ||
        query.data.errorCode === 'credential_rejected') && (
        <StatePanel
          kind="error"
          title="Credential rejected"
          description="Check the permanent API key and NexHealth subdomain, then try again."
        />
      )}
      {feedback === 'invalid' && (
        <StatePanel
          kind="error"
          title="Check the connection details"
          description="Enter a valid NexHealth subdomain and permanent API key."
        />
      )}
      {(feedback === 'unavailable' ||
        ['nexhealth_unavailable', 'location_payload_invalid'].includes(
          query.data.errorCode ?? ''
        )) && (
        <StatePanel
          kind="error"
          title="NexHealth is temporarily unavailable"
          description="Your setup is saved safely. Retry when the service is available."
        />
      )}
      {(feedback === 'no-locations' ||
        query.data.errorCode === 'no_locations') && (
        <StatePanel
          kind="empty"
          title="No locations found"
          description="The credential is valid, but NexHealth returned no selectable locations. Retry discovery after checking the account."
        />
      )}
      {feedback === 'pending' && (
        <StatePanel
          kind="empty"
          title="Connection is already being checked"
          description="Refresh in a moment to resume the same operation."
        />
      )}

      {!showForm && (
        <div className={styles.connected}>
          <strong>Connected</strong>
          <span>{storedSubdomain}</span>
          <p>Scheduling setup is not complete yet.</p>
        </div>
      )}

      {showForm && (
        <form className={styles.form} onSubmit={(event) => void submit(event)}>
          <label>
            <span>NexHealth subdomain</span>
            <input
              autoComplete="off"
              disabled={working}
              maxLength={63}
              placeholder="myclinic"
              value={subdomain}
              onChange={(event) => setSubdomain(event.target.value)}
            />
          </label>
          <label>
            <span>Permanent API key</span>
            <input
              autoComplete="new-password"
              disabled={working}
              name="nexhealth-credential"
              type="password"
              value={credential}
              onChange={(event) => setCredential(event.target.value)}
            />
          </label>
          <div className={styles.actions}>
            <Button type="submit" disabled={working}>
              {working ? 'Connecting…' : 'Connect'}
            </Button>
            {replacing && (
              <Button
                type="button"
                variant="secondary"
                disabled={working}
                onClick={() => {
                  setCredential('')
                  setReplacing(false)
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      )}

      <div className={styles.actions}>
        {query.data.canRetryDiscovery && !replacing && (
          <Button
            type="button"
            disabled={working}
            onClick={() => void retryDiscovery()}
          >
            Retry discovery
          </Button>
        )}
        {query.data.canReconnect &&
          query.data.connectionStatus !== 'NOT_CONNECTED' &&
          !replacing && (
            <Button
              type="button"
              variant="secondary"
              disabled={working}
              onClick={() => {
                setSubdomain(storedSubdomain)
                setCredential('')
                setReplacing(true)
              }}
            >
              Replace connection
            </Button>
          )}
        <Button
          type="button"
          variant="secondary"
          disabled={working}
          onClick={() => void onSignOut()}
        >
          Sign out
        </Button>
      </div>
    </Card>
  )
}
